import { Lesson } from '@/app/entities/Training'
import { formatDuration } from '@/app/utils/formatDuration'
import { withCdn } from '@/app/utils/withCdn'
import { ClockIcon, ListVideoIcon } from 'lucide-react'
import { useLessonList } from '../providers/LessonListProvider'
import { ViewAttachments } from './ViewAttachments'
import { ViewLessons } from './ViewLessons'
import { YOUTUBE_URL_PATTERN } from '../ClassDetails/components/helpers'

export const getYoutubeEmbedUrl = (url: string) => {
  const regexPatterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([^?]+)/,
  ]

  for (const pattern of regexPatterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return `https://www.youtube-nocookie.com/embed/${match[1]}`
    }
  }

  return ''
}

export function TrainingView() {
  const { currentLesson, training, lessons, setCurrentLesson } = useLessonList()

  if (!currentLesson?.videoUrl) return null

  const handleChangeLesson = (lesson: Lesson) => {
    setCurrentLesson(lesson)
  }

  const index = lessons.findIndex((lesson) => lesson._id === currentLesson?._id)

  const isYoutubeVideo =
    YOUTUBE_URL_PATTERN.test(currentLesson?.videoUrl) ?? false
  const youtubeEmbedUrl = isYoutubeVideo
    ? getYoutubeEmbedUrl(currentLesson.videoUrl)
    : ''

  return (
    <div className="grid grid-cols-4 gap-5">
      <div className="col-span-3 flex flex-col gap-5">
        {isYoutubeVideo && youtubeEmbedUrl ? (
          <div className="relative w-full pt-[56.25%]">
            <iframe
              className="absolute inset-0 h-full w-full rounded-lg"
              src={youtubeEmbedUrl}
              title={currentLesson.name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <video
            className="h-[555px] w-full rounded-lg"
            src={withCdn(currentLesson?.videoUrl)}
            poster={withCdn(currentLesson.thumbnail || '')}
            controls
          />
        )}
        <h1 className="text-xl font-semibold">
          Aula {index + 1}: {currentLesson?.name}
        </h1>
        <div className="flex w-full flex-col gap-2 rounded-md bg-[#F7F7F7] p-4">
          <h1 className="font-semibold">Sobre a aula</h1>
          {currentLesson?.description && (
            <p className="whitespace-pre-wrap text-sm text-gray-500">
              {currentLesson?.description}
            </p>
          )}
        </div>
        {currentLesson?.attachments &&
          currentLesson?.attachments.length > 0 && (
            <div className="flex flex-col gap-[10px]">
              <h1 className="font-semibold">Material de apoio</h1>
              <ViewAttachments attachments={currentLesson?.attachments ?? []} />
            </div>
          )}
      </div>
      <div className="flex min-w-[338px] flex-col gap-5">
        <div className="flex flex-col">
          <div className="flex h-[144px] flex-col gap-2 rounded-2xl rounded-b-none border p-5">
            <h1 className="font-semibold">{training.name}</h1>
            <p className="flex items-center gap-2 text-sm text-gray-500">
              <ClockIcon className="h-4 w-4" />
              {formatDuration(currentLesson?.duration)}
            </p>
            <p className="flex items-center gap-2 text-sm text-gray-500">
              <ListVideoIcon className="h-4 w-4" />
              {training?.lessonsLength} aulas
            </p>
          </div>
          <ViewLessons onLessonChange={handleChangeLesson} lessons={lessons} />
        </div>
      </div>
    </div>
  )
}
